import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import RichText from "@/components/RichText";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/dialogs";
import { ShieldCheck, FileText, RefreshCw, Send, Eye, Link2, Pencil, Plus, Trash2 } from "lucide-react";

const STATUS = {
  draft: { label: "Draft — ready for review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  awaiting_response: { label: "Sent — awaiting response", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  sent: { label: "Sent", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  response_received: { label: "Response received", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  part_received: { label: "Part received", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  complete: { label: "Complete", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  attention_required: { label: "Attention required", cls: "bg-red-50 text-red-700 border-red-200" },
};
const RESP_OPTS = [["response_received", "Response received"], ["part_received", "Part received"], ["complete", "Complete"], ["attention_required", "Attention required"]];
const EMPTY_ACC = { firm: "", contact: "", email: "", phone: "", address: "" };

export default function ClearanceCard({ proposalId, proposalStatus }) {
  const { user } = useAuth();
  const canEdit = !["read_only"].includes(user?.role);
  const canCreate = proposalStatus === "accepted";
  const [cl, setCl] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [letter, setLetter] = useState("");
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [issuedOpen, setIssuedOpen] = useState(false);
  const [acc, setAcc] = useState(EMPTY_ACC);
  const [inds, setInds] = useState([]);

  const load = () => api.get(`/clearance/by-proposal/${proposalId}`).then((r) => setCl(r.data)).catch(() => setCl(null)).finally(() => setLoaded(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [proposalId]);

  const openCreate = async () => {
    setAcc(EMPTY_ACC); setInds([]);
    setCreateOpen(true);
    try {
      const { data } = await api.get(`/clearances/prefill/${proposalId}`);
      if (data.accountant) setAcc({ ...EMPTY_ACC, ...data.accountant });
      if (Array.isArray(data.individuals)) setInds(data.individuals);
    } catch { /* no onboarding to prefill from */ }
  };
  const addInd = () => setInds((x) => [...x, { name: "", utr: "", ni_number: "" }]);
  const updInd = (i, k, v) => setInds((x) => x.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const delInd = (i) => setInds((x) => x.filter((_, j) => j !== i));
  const submitCreate = async () => {
    if (!acc.email.trim()) { toast.error("Previous accountant email is required"); return; }
    setBusy(true);
    try {
      await api.post("/clearances", { proposal_id: proposalId, accountant: acc, individuals: inds.filter((p) => p.name.trim()) });
      toast.success("Professional clearance drafted");
      setCreateOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create clearance"); }
    finally { setBusy(false); }
  };

  if (!loaded) return null;

  if (!cl) {
    if (!canCreate) return null;
    return (
      <Card className="p-5 rounded-xl" data-testid="clearance-card-empty">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="w-5 h-5 text-cass-teal" />
          <h3 className="font-alt font-semibold text-cass-navy">Professional Clearance</h3>
        </div>
        <p className="text-sm text-cass-slate mb-4">No clearance for this proposal yet. You can raise one for the previous accountant — the right letter template (company or sole trader / individual) is chosen automatically from the client's business type.</p>
        {canEdit && <Button size="sm" className="bg-cass-navy text-white" onClick={openCreate} data-testid="clearance-create-open"><Plus className="w-4 h-4 mr-1" />Create clearance</Button>}
        <CreateDialog {...{ createOpen, setCreateOpen, acc, setAcc, inds, addInd, updInd, delInd, submitCreate, busy }} />
      </Card>
    );
  }

  const sent = ["sent", "awaiting_response"].includes(cl.status);
  const st = STATUS[cl.status] || { label: cl.status, cls: "bg-slate-50 text-slate-600 border-slate-200" };
  const link = cl.token ? `${window.location.origin}/clearance/${cl.token}` : null;

  const openEdit = () => { setLetter(cl.letter_html || ""); setEditOpen(true); };
  const saveLetter = async () => {
    setBusy(true);
    try { await api.put(`/clearances/${cl.id}`, { letter_html: letter }); toast.success("Clearance letter saved"); setEditOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };
  const regenerate = async () => {
    if (!(await confirmDialog({ title: "Regenerate letter?", description: "Rebuilds the letter from the master template and current service blocks. Any manual edits to this client's letter will be lost.", confirmText: "Regenerate" }))) return;
    try { await api.post(`/clearances/${cl.id}/regenerate`); toast.success("Letter regenerated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not regenerate"); }
  };
  const send = async () => {
    if (!(await confirmDialog({ title: "Approve & send clearance?", description: `This finalises the PDF and emails ${cl.accountant?.firm || "the previous accountant"} a secure download link. It can't be edited afterwards.`, confirmText: "Approve & send" }))) return;
    setBusy(true);
    try { const { data } = await api.post(`/clearances/${cl.id}/send`); toast.success(data.delivered ? "Clearance sent" : "Clearance finalised (email delivery could not be confirmed)"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 rounded-xl" data-testid="clearance-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-cass-teal" />
          <h3 className="font-alt font-semibold text-cass-navy">Professional Clearance</h3>
        </div>
        <Badge variant="outline" className={st.cls} data-testid="clearance-status">{st.label}</Badge>
      </div>
      <div className="text-sm text-cass-slate space-y-0.5 mb-4">
        <div>Previous accountant: <span className="text-cass-navy font-medium">{cl.accountant?.firm || "—"}</span>{cl.accountant?.contact ? ` (${cl.accountant.contact})` : ""}</div>
        <div>{cl.accountant?.email}</div>
        {cl.sent_at && <div className="text-xs">Sent {new Date(cl.sent_at).toLocaleString("en-GB")}{cl.issued_by ? ` by ${cl.issued_by}` : ""}</div>}
        {cl.snapshot && <div className="text-xs text-emerald-700" data-testid="clearance-snapshot-note">Issued version preserved (immutable) · <button className="underline" onClick={() => setIssuedOpen(true)} data-testid="clearance-view-issued">View issued letter</button></div>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => window.open(`${API}/clearances/${cl.id}/pdf`, "_blank")} data-testid="clearance-preview"><Eye className="w-4 h-4 mr-1" />Preview PDF</Button>
        {canEdit && !sent && <Button size="sm" variant="outline" onClick={openEdit} data-testid="clearance-edit"><Pencil className="w-4 h-4 mr-1" />Edit letter</Button>}
        {canEdit && !sent && <Button size="sm" variant="outline" onClick={regenerate} data-testid="clearance-regenerate"><RefreshCw className="w-4 h-4 mr-1" />Regenerate</Button>}
        {canEdit && !sent && <Button size="sm" className="bg-cass-navy text-white" onClick={send} disabled={busy} data-testid="clearance-send"><Send className="w-4 h-4 mr-1" />Approve &amp; send</Button>}
        {sent && link && <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(link); toast.success("Secure link copied"); }} data-testid="clearance-copy-link"><Link2 className="w-4 h-4 mr-1" />Copy secure link</Button>}
      </div>

      {sent && (
        <div className="mt-4 border-t border-border pt-3" data-testid="clearance-response">
          <div className="text-xs font-semibold text-cass-navy mb-2">Track response</div>
          <div className="flex flex-wrap items-center gap-2">
            {RESP_OPTS.map(([v, l]) => (
              <Button key={v} size="sm" variant={cl.status === v ? "default" : "outline"} className={cl.status === v ? "bg-cass-navy text-white h-8" : "h-8"}
                onClick={async () => { try { await api.post(`/clearances/${cl.id}/response`, { status: v, response_date: new Date().toISOString().slice(0, 10) }); toast.success("Status updated"); load(); } catch { toast.error("Could not update"); } }}
                data-testid={`clearance-resp-${v}`}>{l}</Button>
            ))}
          </div>
          {cl.response_notes && <p className="text-xs text-cass-slate mt-2">{cl.response_notes}</p>}
        </div>
      )}

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="clearance-edit-dialog">
          <DialogHeader><DialogTitle>Edit clearance letter — {cl.client_name}</DialogTitle><DialogDescription>Edits apply to this client's letter only and never change the master template.</DialogDescription></DialogHeader>
          <RichText value={letter} onChange={setLetter} testId="clearance-letter-editor" />
          <DialogFooter><Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button><Button className="bg-cass-teal text-white" onClick={saveLetter} disabled={busy} data-testid="clearance-letter-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={issuedOpen} onOpenChange={setIssuedOpen}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="clearance-issued-dialog">
          <DialogHeader><DialogTitle>Issued clearance letter — {cl.client_name}</DialogTitle><DialogDescription>The exact letter issued to {cl.snapshot?.recipient?.firm || "the previous accountant"} on {cl.sent_at ? new Date(cl.sent_at).toLocaleDateString("en-GB") : ""}. This is a preserved historical record and cannot change.</DialogDescription></DialogHeader>
          <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: cl.snapshot?.letter_html || "" }} />
          {cl.snapshot?.blocks?.length > 0 && (
            <div className="mt-4 border-t border-border pt-3" data-testid="clearance-issued-blocks">
              <div className="text-xs font-semibold text-cass-navy mb-1">Requirement blocks used (exact versions)</div>
              <ul className="text-xs text-cass-slate space-y-0.5">
                {cl.snapshot.blocks.map((b) => (
                  <li key={b.id} data-testid={`clearance-issued-block-${b.id}`}>
                    <span className="text-cass-navy">{b.name || b.heading}</span>
                    {b.version_id ? <span className="font-mono text-[10px]"> · v{String(b.version_id).slice(0, 8)}</span> : ""}
                    {b.version_saved_at ? ` · ${new Date(b.version_saved_at).toLocaleDateString("en-GB")}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function CreateDialog({ createOpen, setCreateOpen, acc, setAcc, inds, addInd, updInd, delInd, submitCreate, busy }) {
  const f = (k) => (e) => setAcc((a) => ({ ...a, [k]: e.target.value }));
  return (
    <Dialog open={createOpen} onOpenChange={setCreateOpen}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="clearance-create-dialog">
        <DialogHeader>
          <DialogTitle>Create professional clearance</DialogTitle>
          <DialogDescription>Enter the previous accountant's details. Self Assessment individuals are prefilled from onboarding where available and can be edited.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Previous accountant — firm</Label><Input value={acc.firm} onChange={f("firm")} data-testid="clc-firm" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Contact name</Label><Input value={acc.contact} onChange={f("contact")} data-testid="clc-contact" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={acc.phone} onChange={f("phone")} data-testid="clc-phone" /></div>
          </div>
          <div><Label className="text-xs">Email <span className="text-red-500">*</span></Label><Input type="email" value={acc.email} onChange={f("email")} data-testid="clc-email" /></div>
          <div><Label className="text-xs">Address</Label><Input value={acc.address} onChange={f("address")} data-testid="clc-address" /></div>

          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Self Assessment individuals</Label>
              <Button type="button" size="sm" variant="outline" className="h-7" onClick={addInd} data-testid="clc-add-individual"><Plus className="w-3.5 h-3.5 mr-1" />Add person</Button>
            </div>
            {inds.length === 0 && <p className="text-xs text-cass-slate">None. Add anyone whose personal Self Assessment we'll request records for.</p>}
            {inds.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end mt-2" data-testid={`clc-individual-${i}`}>
                <div><Label className="text-[10px]">Name</Label><Input value={p.name} onChange={(e) => updInd(i, "name", e.target.value)} data-testid={`clc-ind-name-${i}`} /></div>
                <div><Label className="text-[10px]">UTR</Label><Input value={p.utr} onChange={(e) => updInd(i, "utr", e.target.value)} data-testid={`clc-ind-utr-${i}`} /></div>
                <div><Label className="text-[10px]">NI number</Label><Input value={p.ni_number} onChange={(e) => updInd(i, "ni_number", e.target.value)} data-testid={`clc-ind-ni-${i}`} /></div>
                <Button type="button" size="icon" variant="ghost" className="h-9 w-9 text-red-500" onClick={() => delInd(i)} data-testid={`clc-ind-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button className="bg-cass-navy text-white" onClick={submitCreate} disabled={busy} data-testid="clearance-create-submit">Create draft</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
